import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import GoogleSignInButton from '../components/Auth/GoogleSignInButton';
import GoogleOneTap from '../components/Auth/GoogleOneTap';
import LegalModal from '../components/Legal/LegalModal';
import PrivacyPolicyContent from '../components/Legal/PrivacyPolicyContent';
import TermsOfServiceContent from '../components/Legal/TermsOfServiceContent';
import AuthShell, { inputClass, labelClass, buttonClass, Notice, Divider } from '../components/Auth/AuthShell';

const Register = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    password_confirm: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const { register, login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Basic validation
    if (formData.password !== formData.password_confirm) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      const result = await register(formData);
      if (result.success) {
        // Sign straight in; the verification email can be clicked later
        const signedIn = await login(formData.email, formData.password);
        if (signedIn.success) {
          navigate('/dashboard');
          return;
        }
        setSuccess(result.message || 'Registration successful! Please check your email to verify your account.');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        // Handle validation errors
        if (typeof result.error === 'object') {
          const errorMessages = [];
          for (const [field, messages] of Object.entries(result.error)) {
            if (Array.isArray(messages)) {
              errorMessages.push(...messages);
            } else {
              errorMessages.push(messages);
            }
          }
          setError(errorMessages.join('. '));
        } else {
          setError(result.error || 'Registration failed');
        }
      }
    } catch (err) {
      setError('An error occurred during registration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create your account"
      subtitle={<>Already have an account? <Link to="/login" className="font-medium text-primary-300 hover:text-primary-200">Sign in</Link></>}
    >
      <GoogleOneTap />
      <GoogleSignInButton variant="light" text="Sign up with Google" className="w-full !rounded-lg !py-3" disabled={loading} />

      <Divider>or sign up with email</Divider>

      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="first_name" className={labelClass}>First name</label>
            <input id="first_name" name="first_name" type="text" autoComplete="given-name" required
              value={formData.first_name} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label htmlFor="last_name" className={labelClass}>Last name</label>
            <input id="last_name" name="last_name" type="text" autoComplete="family-name" required
              value={formData.last_name} onChange={handleChange} className={inputClass} />
          </div>
        </div>

        <div>
          <label htmlFor="email" className={labelClass}>Email</label>
          <input id="email" name="email" type="email" autoComplete="email" required
            value={formData.email} onChange={handleChange} className={inputClass} placeholder="you@example.com" />
        </div>

        <div>
          <label htmlFor="password" className={labelClass}>Password</label>
          <div className="relative">
            <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required minLength={8}
              value={formData.password} onChange={handleChange} className={`${inputClass} pr-11`} aria-describedby="password-hint" />
            <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-white"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
          </div>
          <p id="password-hint" className="mt-1.5 text-xs text-gray-500">At least 8 characters.</p>
        </div>

        <div>
          <label htmlFor="password_confirm" className={labelClass}>Confirm password</label>
          <div className="relative">
            <input id="password_confirm" name="password_confirm" type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password" required
              value={formData.password_confirm} onChange={handleChange} className={`${inputClass} pr-11`} />
            <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-white"
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
          </div>
        </div>

        {error && <Notice>{error}</Notice>}
        {success && <Notice tone="success">{success}</Notice>}

        <button type="submit" disabled={loading} className={buttonClass}>
          {loading ? <span className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" /> : 'Create account'}
        </button>

        <p className="text-center text-xs text-gray-500">
          By creating an account you agree to our{' '}
          <button type="button" onClick={() => setShowTermsModal(true)} className="text-primary-300 hover:underline">Terms of Service</button>
          {' '}and{' '}
          <button type="button" onClick={() => setShowPrivacyModal(true)} className="text-primary-300 hover:underline">Privacy Policy</button>.
        </p>
      </form>

      <LegalModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} title="Privacy Policy" lastUpdated="Last updated: April 19, 2026">
        <PrivacyPolicyContent />
      </LegalModal>
      <LegalModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} title="Terms of Service" lastUpdated="Last updated: April 19, 2026">
        <TermsOfServiceContent />
      </LegalModal>
    </AuthShell>
  );
};

export default Register;
